import { AppBarEvents } from "@1js/office-start-appbar-events";
import {
  Button,
  mergeClasses,
  OnOpenChangeData,
  OpenPopoverEvents,
  tokens,
  Tooltip,
  useModalAttributes,
} from "@fluentui/react-components";
import { useNovaEventing } from "@nova/react";
import React, { useMemo, useState } from "react";
import { AppBarErrorBoundary } from "./AppBarErrorBoundary";
import { ViewMode } from "./Constants";
import { useFlyoutButtonStyles } from "./FlyoutButton.styles";
import { EventType, FlyoutButtonProps } from "./FlyoutButton.types";
import useAppBarContext from "./hooks/useAppBarContext";
import { AppsIcon, AppsIconSelected } from "./Icons";
import { TileIcon } from "./TileIcon";
/**
 * Button that launches the More Apps flyout when clicked
 */
export const FlyoutButtonRender: React.ForwardRefRenderFunction<
  HTMLButtonElement | HTMLAnchorElement,
  FlyoutButtonProps
> = (
  {
    icon,
    selectedIcon,
    selected,
    text,
    title,
    emptyStateMessage,
    suggestedAppsAddButtonText,
    suggestedAppsPrivacyLinkText,
    suggestedAppsPermissionsLinkText,
    suggestedAppsTermsOfUseLinkText,
    suggestedAppsSectionTitle,
    quickAccessSectionTitle,
    seeAllButtonLabel,
    seeAllButtonEnabled,
    seeAllButtonIndex,
    wide,
    launchTileBehavior,
    enabledFlyoutButtonBadgeType,
    flyoutButtonBadgeDescription,
    flyoutSize,
    footerLinks,
    footerLinkFormat,
    searchEnabled,
    suggestionsEnabled,
    quickAccessEnabled,
    searchBoxPlaceholderText,
    searchDebounceRate,
    searchNoResultsMessage,
    searchNoResultsTitle,
    searchResultsSectionTitle,
    quickAccessSearchResultsSectionTitle,
    installedAppsSectionAriaLabel,
    searchResultsAriaLiveAnnouncement,
    suggestedAppsSectionAriaLabel,
    quickAccessSectionAriaLabel,
    installedAppsAnnouncementMessage,
    suggestedAppsAnnouncementMessage,
    installedAndSuggestedAppsAnnouncementMessage,
    enableVisualRefreshStyles,
    onPinStateChange,
    customImportHandler = (p) => p,
  },
  forwardRef
) => {
  const styles = useFlyoutButtonStyles();
  const [flyoutOpen, setFlyoutOpen] = useState<boolean | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipVisibility, setTooltipVisibility] = useState<boolean>(false);
  const [blockTooltip, setBlockTooltip] = useState<boolean>(false);
  const targetRef = React.useRef<HTMLElement | null>(null);
  /**
   * Sets the selected state of the flyout icon
   * Note: Selected state is only activated when a host app overrides the flyout icon
   */
  const iconSelectedState = React.useMemo(() => {
    return icon && (selected || isHovered || flyoutOpen);
  }, [flyoutOpen, icon, isHovered, selected]);
  const novaEventing = useNovaEventing();
  const appBarContext = useAppBarContext();
  const changeFlyoutState = React.useCallback(
    (state: boolean) => {
      setFlyoutOpen(state);
      if (state) {
        const flyoutOpenedEvent = AppBarEvents.flyoutOpened(() => {
          return {
            overflowCount: appBarContext?.overflowItems?.length ?? -1,
          };
        });
        novaEventing.generateEvent({
          event: flyoutOpenedEvent,
        });
      }
    },
    [novaEventing, appBarContext?.overflowItems]
  );
  const closeFlyout = React.useCallback(() => {
    changeFlyoutState(false);
    setBlockTooltip(true);
  }, [changeFlyoutState]);
  React.useEffect(() => {
    window.addEventListener(EventType.RESIZE, closeFlyout);
    window.addEventListener(EventType.SCROLL, closeFlyout);
    return () => {
      window.removeEventListener(EventType.RESIZE, closeFlyout);
      window.removeEventListener(EventType.SCROLL, closeFlyout);
    };
  });
  const onMouseEnter = () => {
    const flyoutHoverEvent = AppBarEvents.flyoutButtonHover(() => {
      return {
        isHovered: true,
      };
    });
    novaEventing.generateEvent({
      event: flyoutHoverEvent,
    });
    setIsHovered(true);
  };
  const onMouseLeave = () => {
    const flyoutHoverEvent = AppBarEvents.flyoutButtonHover(() => {
      return {
        isHovered: false,
      };
    });
    novaEventing.generateEvent({
      event: flyoutHoverEvent,
    });
    setIsHovered(false);
  };
  const buttonBehavior = {
    onMouseOver: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onContextMenu: (evt: React.MouseEvent<HTMLElement>) => {
      evt.preventDefault();
    },
  };
  /**
   * If icons are specified via control data, use those
   * otherwise use sized AppsIcon from internal icon set
   */
  const buttonIcon = React.useMemo(() => {
    return iconSelectedState
      ? (selectedIcon ?? (
          <AppsIconSelected
            size={wide ? "wide" : "default"}
            primaryFill={tokens.colorNeutralForeground3BrandSelected}
          />
        ))
      : (icon ?? (
          <AppsIcon
            size={wide ? "wide" : "default"}
            primaryFill={
              flyoutOpen || isHovered
                ? tokens.colorNeutralForeground3BrandSelected
                : tokens.colorNeutralForeground3
            }
          />
        ));
  }, [flyoutOpen, icon, iconSelectedState, isHovered, selectedIcon, wide]);
  const onClickHandler = () => {
    changeFlyoutState(!flyoutOpen);
  };
  const setButtonRef: React.Ref<HTMLAnchorElement | HTMLButtonElement> = (
    buttonRef
  ) => {
    // Set the ref to be used in the flyout as trigger target
    targetRef.current = buttonRef;
    // Set the ref for the forwarded ref from the parent
    if (typeof forwardRef === "function") {
      forwardRef(buttonRef);
    } else if (forwardRef) {
      forwardRef.current = buttonRef;
    }
  };
  const LazyFlyoutButtonBadgeRenderer = React.useRef(
    React.lazy(() =>
      customImportHandler(
        import(
          /* webpackChunkName: "appbar-control-flyoutbuttonbadge" */ "./FlyoutButtonBadge"
        )
      )
    )
  );
  const badgeControl = useMemo(() => {
    if (
      enabledFlyoutButtonBadgeType &&
      enabledFlyoutButtonBadgeType !== "None" &&
      flyoutButtonBadgeDescription
    ) {
      const LazyFlyoutButtonBadge = LazyFlyoutButtonBadgeRenderer.current;
      return (
        <AppBarErrorBoundary boundarySource="FlyoutButtonBadgeControl">
          <React.Suspense fallback="">
            <LazyFlyoutButtonBadge
              enabledFlyoutButtonBadgeType={enabledFlyoutButtonBadgeType}
              flyoutButtonBadgeDescription={flyoutButtonBadgeDescription}
            />
          </React.Suspense>
        </AppBarErrorBoundary>
      );
    }
    return null;
  }, [enabledFlyoutButtonBadgeType, flyoutButtonBadgeDescription]);
  const { triggerAttributes } = useModalAttributes();
  const flyoutButton = (
    <Tooltip
      content={title}
      relationship="label"
      positioning="after"
      visible={tooltipVisibility}
      onVisibleChange={(_event, data) => {
        if (blockTooltip && data.visible) {
          setTooltipVisibility(false);
          setBlockTooltip(false);
        } else {
          setTooltipVisibility(data.visible);
        }
      }}
    >
      <div
        className={mergeClasses(
          styles.moreAppsButtonContainer,
          enableVisualRefreshStyles && styles.roundedTileButtonContainer
        )}
      >
        <Button
          ref={setButtonRef}
          data-tid="appbar-flyout-button"
          onClick={onClickHandler}
          aria-haspopup="dialog"
          iconPosition="after"
          aria-label={title}
          tabIndex={0}
          icon={
            <TileIcon
              id="appbar-flyout-button-icon"
              icon={buttonIcon}
              viewMode={wide ? ViewMode.Wide : ViewMode.Default}
              label={text}
              isSelected={!!iconSelectedState}
              brandingColor=""
              showDotIndicator={false}
            />
          }
          className={mergeClasses(
            styles.moreAppsButton,
            wide && styles.wideMoreAppsButton,
            !!iconSelectedState && styles.moreAppsButtonSelected, // Apply the selected style when the icon is in selected state
            enableVisualRefreshStyles && styles.roundedTileButton,
            !!iconSelectedState &&
              enableVisualRefreshStyles &&
              styles.roundedTileButtonSelected
          )}
          {...buttonBehavior}
          {...triggerAttributes}
        />
        {badgeControl}
      </div>
    </Tooltip>
  );
  const handleOpenChange = React.useCallback(
    (e: OpenPopoverEvents, data: OnOpenChangeData) => {
      if (!targetRef.current?.contains(e.target as HTMLElement)) {
        // Only handle the open change event from the popover if
        // the target of the event did not happen in the flyout button,
        // which is denoted by targetRef, or one of its descendents in the
        // DOM (e.g. the tile icon).  If the event happened from the flyout
        // button, its click handler will handle the event.
        // See Custom Trigger documentation on Popover for details.
        changeFlyoutState(data.open);
      }
    },
    [changeFlyoutState]
  );
  // Lazy renderer for the flyout - using a ref
  // here so it's sticky across renders
  const LazyFlyoutRenderer = React.useRef(
    React.lazy(() =>
      customImportHandler(
        import(
          /* webpackChunkName: "appbar-control-lazyflyout" */ "./LazyFlyout"
        )
      )
    )
  );
  // Memoizing the flyout so it doesn't flash as you hover over the flyout button,
  // which causes it to re-render
  const lazyFlyout = React.useMemo(() => {
    const LazyFlyout = LazyFlyoutRenderer.current;
    return flyoutOpen != null && targetRef.current != null ? (
      <AppBarErrorBoundary boundarySource="FlyoutButtonLazyFlyout">
        <React.Suspense fallback="">
          <LazyFlyout
            targetRef={targetRef.current}
            ariaLabel={title}
            emptyStateMessage={emptyStateMessage}
            suggestedAppsAddButtonText={suggestedAppsAddButtonText}
            suggestedAppsPrivacyLinkText={suggestedAppsPrivacyLinkText}
            suggestedAppsPermissionsLinkText={suggestedAppsPermissionsLinkText}
            suggestedAppsTermsOfUseLinkText={suggestedAppsTermsOfUseLinkText}
            suggestedAppsSectionTitle={suggestedAppsSectionTitle}
            quickAccessSectionTitle={quickAccessSectionTitle}
            seeAllButtonLabel={seeAllButtonLabel}
            seeAllButtonEnabled={seeAllButtonEnabled}
            seeAllButtonIndex={seeAllButtonIndex}
            flyoutOpen={!!flyoutOpen}
            handleOpenChange={handleOpenChange}
            launchTileBehavior={launchTileBehavior}
            flyoutSize={flyoutSize}
            closeFlyout={closeFlyout}
            footerLinks={footerLinks}
            footerLinkFormat={footerLinkFormat}
            searchEnabled={searchEnabled}
            suggestionsEnabled={suggestionsEnabled}
            quickAccessEnabled={quickAccessEnabled}
            searchBoxPlaceholderText={searchBoxPlaceholderText}
            searchDebounceRate={searchDebounceRate}
            searchNoResultsTitle={searchNoResultsTitle}
            searchNoResultsMessage={searchNoResultsMessage}
            searchResultsSectionTitle={searchResultsSectionTitle}
            quickAccessSearchResultsSectionTitle={
              quickAccessSearchResultsSectionTitle
            }
            installedAppsSectionAriaLabel={installedAppsSectionAriaLabel}
            searchResultsAriaLiveAnnouncement={
              searchResultsAriaLiveAnnouncement
            }
            suggestedAppsSectionAriaLabel={suggestedAppsSectionAriaLabel}
            quickAccessSectionAriaLabel={quickAccessSectionAriaLabel}
            installedAppsAnnouncementMessage={installedAppsAnnouncementMessage}
            suggestedAppsAnnouncementMessage={suggestedAppsAnnouncementMessage}
            installedAndSuggestedAppsAnnouncementMessage={
              installedAndSuggestedAppsAnnouncementMessage
            }
            onPinStateChange={onPinStateChange}
            customImportHandler={customImportHandler}
          />
        </React.Suspense>
      </AppBarErrorBoundary>
    ) : null;
  }, [
    flyoutOpen,
    title,
    emptyStateMessage,
    suggestedAppsAddButtonText,
    suggestedAppsPrivacyLinkText,
    suggestedAppsPermissionsLinkText,
    suggestedAppsTermsOfUseLinkText,
    suggestedAppsSectionTitle,
    quickAccessSectionTitle,
    seeAllButtonLabel,
    seeAllButtonEnabled,
    seeAllButtonIndex,
    handleOpenChange,
    launchTileBehavior,
    flyoutSize,
    closeFlyout,
    footerLinks,
    footerLinkFormat,
    searchEnabled,
    suggestionsEnabled,
    quickAccessEnabled,
    searchBoxPlaceholderText,
    searchDebounceRate,
    searchNoResultsTitle,
    searchNoResultsMessage,
    searchResultsSectionTitle,
    quickAccessSearchResultsSectionTitle,
    installedAppsSectionAriaLabel,
    searchResultsAriaLiveAnnouncement,
    suggestedAppsSectionAriaLabel,
    quickAccessSectionAriaLabel,
    installedAppsAnnouncementMessage,
    suggestedAppsAnnouncementMessage,
    installedAndSuggestedAppsAnnouncementMessage,
    onPinStateChange,
    customImportHandler,
  ]);
  return (
    <>
      {flyoutButton}
      {lazyFlyout}
    </>
  );
};
FlyoutButtonRender.displayName = "FlyoutButton";
export const FlyoutButton = React.forwardRef(FlyoutButtonRender);
